/**
 * corporate-api.js · thin client for the /api/corporate/* endpoints.
 *
 * Everything runs with `credentials: 'include'` so the httpOnly
 * `lc_corp_session` cookie is sent on every call once the member has
 * signed in. Errors are normalised to Error objects with a `.status`
 * so callers can branch on 401 (redirect to sign-in) vs 403 (T&C gate)
 * without unpacking a Response.
 */
const API = process.env.REACT_APP_BACKEND_URL || "";

async function _call(path, { method = "GET", body, headers = {} } = {}) {
  const res = await fetch(`${API}${path}`, {
    method,
    credentials: "include",
    headers: { "Content-Type": "application/json", ...headers },
    body: body != null ? JSON.stringify(body) : undefined,
  });

  let payload = null;
  const ct = res.headers.get("content-type") || "";
  if (ct.includes("application/json")) {
    payload = await res.json().catch(() => null);
  }

  if (!res.ok) {
    const detail = payload?.detail;
    const msg =
      typeof detail === "string"
        ? detail
        : Array.isArray(detail)
        ? detail.map((e) => e?.msg || JSON.stringify(e)).join(" · ")
        : `Request failed (${res.status})`;
    const err = new Error(msg);
    err.status = res.status;
    err.payload = payload;
    throw err;
  }
  return payload;
}

export const corporateApi = {
  // ── Public ──
  portal:  (slug) => _call(`/api/corporate/portal/${encodeURIComponent(slug)}`),

  // ── Auth flow ──
  signInStart: (slug, { email, first_name, last_name }) =>
    _call(`/api/corporate/${encodeURIComponent(slug)}/sign-in`, {
      method: "POST",
      body:   { email, first_name, last_name },
    }),
  signInVerify: ({ slug, email, otp }) =>
    _call(`/api/corporate/sign-in/verify`, {
      method: "POST",
      body:   { slug, email, otp },
    }),
  signOut: () => _call(`/api/corporate/sign-out`, { method: "POST" }),

  // ── Session ──
  me:           () => _call(`/api/corporate/me`),
  miraChatbaseIdentity: (payload) =>
    _call(`/api/mira/chatbase-identity`, { method: "POST", body: payload }),
  guestSession: (slug) =>
    _call(`/api/corporate/${encodeURIComponent(slug)}/guest-session`, {
      method: "POST",
    }),
  acceptTerms:  (terms_version) =>
    _call(`/api/corporate/accept-terms`, {
      method: "POST",
      body:   { terms_version },
    }),

  // ── Brief ──
  submitBrief:  (payload) =>
    _call(`/api/corporate/brief`, { method: "POST", body: payload }),

  // ── Admin CRUD (console-admin only) ──
  admin: {
    listTenants:   () => _call(`/api/admin/corporate/tenants`),
    getTenant:     (id) => _call(`/api/admin/corporate/tenants/${id}`),
    createTenant:  (body) =>
      _call(`/api/admin/corporate/tenants`, { method: "POST", body }),
    updateTenant:  (id, body) =>
      _call(`/api/admin/corporate/tenants/${id}`, { method: "PATCH", body }),
    softDeleteTenant: (id) =>
      _call(`/api/admin/corporate/tenants/${id}`, { method: "DELETE" }),
    listMembers:   (id) =>
      _call(`/api/admin/corporate/tenants/${id}/members`),
    inviteMember:  (id, body) =>
      _call(`/api/admin/corporate/tenants/${id}/members/invite`, {
        method: "POST", body,
      }),
    offboardMember: (tid, mid) =>
      _call(`/api/admin/corporate/tenants/${tid}/members/${mid}/offboard`, {
        method: "POST",
      }),
  },
};
